import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { EXPENSE_CATEGORIES, CURRENCIES } from "@/lib/expenseConstants";
import { UserAvatar } from "@/components/Avatars";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const ExpenseDialog = ({ workspaceId, members, projects, myId, open, onOpenChange }) => {
  const qc = useQueryClient();
  const active = members.filter((m) => m.status === "active");
  const [entryType, setEntryType] = useState("expense");
  const [concept, setConcept] = useState("");
  const [amount, setAmount] = useState("");
  const [payerId, setPayerId] = useState(myId);
  const [selected, setSelected] = useState([]);
  const [method, setMethod] = useState("equal");
  const [values, setValues] = useState({});
  const [showMore, setShowMore] = useState(false);
  const [category, setCategory] = useState("other");
  const [currency, setCurrency] = useState("EUR");
  const [date, setDate] = useState("");
  const [projectId, setProjectId] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const toggle = (pid) =>
    setSelected((s) => (s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]));

  const reset = () => {
    setConcept(""); setAmount(""); setSelected([]); setMethod("equal"); setValues({});
    setShowMore(false); setCategory("other"); setCurrency("EUR"); setDate(""); setProjectId(""); setDescription("");
    setPayerId(myId);
  };

  const isIncome = entryType === "income";

  const submit = async () => {
    if (!concept.trim()) return toast.error("El concepto es obligatorio");
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Introduce un importe válido");
    if (!isIncome && method !== "none" && selected.length === 0) return toast.error("Selecciona quién participa");
    setSaving(true);
    try {
      await api.post(`/workspaces/${workspaceId}/expenses`, {
        concept, amount: amt, currency, category, entry_type: entryType,
        date: date || null, description,
        payer_id: payerId,
        split_method: isIncome ? "none" : method,
        participants: (isIncome || method === "none") ? [] : selected.map((pid) => ({ profile_id: pid, value: parseFloat(values[pid] || 0) })),
        project_id: projectId || null,
      });
      qc.invalidateQueries({ queryKey: ["expenses", workspaceId] });
      qc.invalidateQueries({ queryKey: ["balances", workspaceId] });
      qc.invalidateQueries({ queryKey: ["budgets", workspaceId] });
      qc.invalidateQueries({ queryKey: ["expenses-report", workspaceId] });
      toast.success(isIncome ? "Ingreso registrado" : "Gasto registrado");
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="sm:max-w-md max-h-[88vh] overflow-y-auto" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Registrar movimiento</DialogTitle>
          <DialogDescription>Diez segundos y listo. El resto es opcional.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-secondary">
            {[["expense", "💸 Gasto"], ["income", "💰 Ingreso"]].map(([v, l]) => (
              <button key={v} data-testid={`entry-type-${v}`} onClick={() => setEntryType(v)}
                className={`py-1.5 rounded-md text-sm font-medium transition-colors ${entryType === v ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-[1fr,110px] gap-2">
            <div className="space-y-1.5">
              <Label>Concepto</Label>
              <Input data-testid="expense-concept-input" value={concept} onChange={(e) => setConcept(e.target.value)}
                placeholder="Supermercado" maxLength={200} autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label>Importe</Label>
              <Input data-testid="expense-amount-input" type="number" min="0" step="0.01" value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{isIncome ? "¿Quién lo recibe?" : "¿Quién pagó?"}</Label>
            <Select value={payerId} onValueChange={setPayerId}>
              <SelectTrigger data-testid="expense-payer-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {active.map((m) => <SelectItem key={m.profile_id} value={m.profile_id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!isIncome && (
          <div className="space-y-1.5">
            <Label>Método de reparto</Label>
            <div className="flex flex-wrap gap-1.5">
              {[["equal", "Igualitario"], ["custom", "Importes"], ["percentage", "Porcentaje"], ["none", "Sin reparto"]].map(([v, l]) => (
                <button key={v} onClick={() => setMethod(v)} data-testid={`expense-split-${v}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${method === v ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          )}
          {!isIncome && method !== "none" && (
            <div className="space-y-1.5">
              <Label>¿Quién participa?</Label>
              <div className="space-y-1.5 max-h-44 overflow-y-auto border rounded-lg p-2">
                {active.map((m) => (
                  <div key={m.profile_id} className="flex items-center gap-2" data-testid={`expense-participant-${m.profile_id}`}>
                    <Checkbox checked={selected.includes(m.profile_id)} onCheckedChange={() => toggle(m.profile_id)}
                      data-testid={`expense-participant-check-${m.profile_id}`} />
                    <UserAvatar name={m.name} color={m.avatar_color} size="sm" className="!h-6 !w-6 !text-[10px]" />
                    <span className="text-sm flex-1">{m.name}</span>
                    {selected.includes(m.profile_id) && method !== "equal" && (
                      <div className="flex items-center gap-1">
                        <Input type="number" min="0" step="0.01" className="h-7 w-20 text-xs"
                          data-testid={`expense-value-input-${m.profile_id}`}
                          value={values[m.profile_id] || ""}
                          onChange={(e) => setValues((v) => ({ ...v, [m.profile_id]: e.target.value }))} />
                        <span className="text-xs text-muted-foreground">{method === "percentage" ? "%" : currency}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <button className="text-xs text-primary font-medium" data-testid="expense-more-toggle"
            onClick={() => setShowMore(!showMore)}>
            {showMore ? "Ocultar opciones" : "Más opciones (fecha, categoría, proyecto...)"}
          </button>
          {showMore && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Categoría</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Fecha</Label>
                <Input type="date" data-testid="expense-date-input" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Moneda</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid="expense-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Proyecto</Label>
                <Select value={projectId || "__none__"} onValueChange={(v) => setProjectId(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="expense-project-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin proyecto</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.icon} {p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Descripción</Label>
                <Input data-testid="expense-description-input" value={description}
                  onChange={(e) => setDescription(e.target.value)} placeholder="Detalles opcionales" maxLength={1000} />
              </div>
            </div>
          )}
          <Button className="w-full" data-testid="expense-submit-btn" onClick={submit} disabled={saving}>
            {saving ? "Guardando..." : isIncome ? "Registrar ingreso" : "Registrar gasto"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
